import { useEffect, useState } from 'react'
import { useRecommendations } from '@/hooks/useRecommendations'
import { useAuth } from '@/hooks/useAuth'
import {
  CheckCircle, XCircle, RefreshCw, TrendingUp, TrendingDown,
  AlertTriangle, Info, Loader2, Shield, Activity, ChevronDown, ChevronUp,
  Users,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { safeFormat } from '../lib/format'
import { clsx } from 'clsx'
import TickerLink from '@/components/ui/TickerLink'
import { PortfolioFitBadge, PortfolioFitNote } from '@/components/ui/PortfolioFitBadge'
import TableSearch from '@/components/ui/TableSearch'
import ExportCSV, { type ColumnDef } from '@/components/ui/ExportCSV'
import type { Consensus, AgentVote, Recommendation } from '@/lib/types'
import PageHeader from '@/components/ui/PageHeader'

const AGENT_COLORS: Record<string, string> = {
  portfolio: 'bg-blue/10 border-blue/20 text-blue-light',
  risk:      'bg-rose/10 border-rose/20 text-rose-light',
  market:    'bg-cyan/10 border-cyan/20 text-cyan',
  aria:      'bg-purple/10 border-purple/20 text-purple-light',
  goals:     'bg-emerald/10 border-emerald/20 text-emerald',
  compliance:'bg-amber/10 border-amber/20 text-amber',
  // Persona agents
  buffett:   'bg-emerald/10 border-emerald/20 text-emerald',
  dalio:     'bg-blue/10 border-blue/20 text-blue-light',
  soros:     'bg-amber/10 border-amber/20 text-amber',
  simons:    'bg-purple/10 border-purple/20 text-purple-light',
  cathie:    'bg-pink/10 border-pink/20 text-pink-400',
  burry:     'bg-rose/10 border-rose/20 text-rose-light',
}

const AGENT_ACRONYMS: Record<string, string> = {
  portfolio: 'POA', risk: 'RMA', market: 'MIA',
  aria: 'ARIA', goals: 'GTA', compliance: 'CMA',
  // Persona agents
  buffett: 'WB', dalio: 'RD', soros: 'GS',
  simons: 'JS', cathie: 'CW', burry: 'MB',
}

// Persona avatar colors (colored circles with initials)
const PERSONA_AVATAR_BG: Record<string, string> = {
  buffett: 'bg-emerald-500',
  dalio:   'bg-blue-500',
  soros:   'bg-amber-500',
  simons:  'bg-purple-500',
  cathie:  'bg-pink-500',
  burry:   'bg-rose-500',
}

const ACTION_BADGE: Record<string, string> = {
  BUY:   'badge-green',
  SELL:  'badge-red',
  HOLD:  'badge-blue',
  ALERT: 'badge-amber',
  INFO:  'badge-blue',
}

const PRIORITY_BADGE: Record<number, { label: string; cls: string }> = {
  5: { label: 'Critical', cls: 'badge-red' },
  4: { label: 'High',     cls: 'badge-amber' },
  3: { label: 'Medium',   cls: 'badge-blue' },
  2: { label: 'Low',      cls: 'badge-green' },
  1: { label: 'Info',     cls: 'badge-blue' },
}

const AGENT_FULL_NAMES: Record<string, string> = {
  portfolio: 'Portfolio Optimiser',
  risk: 'Risk Manager',
  market: 'Market Intelligence',
  aria: 'ARIA Behavioural',
  goals: 'Goal Tracker',
  compliance: 'Compliance Monitor',
  // Persona agents
  buffett: 'Warren Buffett (Value)',
  dalio: 'Ray Dalio (Macro)',
  soros: 'George Soros (Reflexivity)',
  simons: 'Jim Simons (Quant)',
  cathie: 'Cathie Wood (Innovation)',
  burry: 'Michael Burry (Contrarian)',
}

const TABS = [
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all',      label: 'All' },
] as const

function fmt(n: number, prefix = '£') {
  return `${prefix}${n.toLocaleString('en-GB', { maximumFractionDigits: 0 })}`
}

function AgreementBadge({ pct }: { pct: number }) {
  const isStrong = pct >= 67
  const isContested = pct < 50
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold',
        isStrong && 'bg-emerald/10 text-emerald border border-emerald/20',
        !isStrong && !isContested && 'bg-amber/10 text-amber border border-amber/20',
        isContested && 'bg-rose/10 text-rose-light border border-rose/20',
      )}
    >
      <Users size={11} aria-hidden />
      {pct}% {isContested ? 'Contested' : 'Agent Agreement'}
    </span>
  )
}

function ConsensusPanel({ consensus }: { consensus: Consensus }) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      {/* Collapsed header */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 bg-surface-hover/50 hover:bg-surface-hover transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <AgreementBadge pct={consensus.agreement_pct} />
          <span className="text-xs text-text-muted">
            {consensus.agent_votes.length} agent{consensus.agent_votes.length !== 1 ? 's' : ''} voted
          </span>
        </div>
        {expanded
          ? <ChevronUp size={14} className="text-text-muted" aria-hidden />
          : <ChevronDown size={14} className="text-text-muted" aria-hidden />
        }
      </button>

      {/* Expanded detail */}
      {expanded && (
        <div className="px-3 py-3 space-y-3 border-t border-border">
          {/* Agent votes */}
          <div className="space-y-2">
            <span className="text-xs font-semibold text-text-muted uppercase tracking-wide">Agent Votes</span>
            {consensus.agent_votes.map((vote: AgentVote) => {
              const agreesWithConsensus = vote.action === consensus.consensus_action
              const agentColor = AGENT_COLORS[vote.agent_id] ?? 'bg-surface-card border-border text-text-muted'
              return (
                <div key={vote.agent_id} className="flex items-start gap-2">
                  {PERSONA_AVATAR_BG[vote.agent_id] ? (
                    <div className={clsx(
                      'w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-bold text-white shrink-0 mt-0.5',
                      PERSONA_AVATAR_BG[vote.agent_id],
                    )}>
                      {AGENT_ACRONYMS[vote.agent_id] ?? vote.agent_id.slice(0, 2).toUpperCase()}
                    </div>
                  ) : (
                    <div className={clsx(
                      'w-6 h-6 rounded border flex items-center justify-center text-[9px] font-bold shrink-0 mt-0.5',
                      agentColor,
                    )}>
                      {AGENT_ACRONYMS[vote.agent_id] ?? vote.agent_id.slice(0, 3).toUpperCase()}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-text-primary">
                        {AGENT_FULL_NAMES[vote.agent_id] ?? vote.agent_id}
                      </span>
                      <span className={clsx(
                        'badge text-[10px] px-1.5 py-0',
                        ACTION_BADGE[vote.action] ?? 'badge-blue',
                      )}>
                        {vote.action}
                      </span>
                      {agreesWithConsensus ? (
                        <CheckCircle size={11} className="text-emerald shrink-0" aria-hidden />
                      ) : (
                        <XCircle size={11} className="text-rose-light shrink-0" aria-hidden />
                      )}
                    </div>
                    {/* Confidence bar */}
                    <div className="flex items-center gap-1.5 mt-1">
                      <div className="w-16 h-1 bg-surface-hover rounded-full">
                        <div
                          className={clsx(
                            'h-full rounded-full',
                            agreesWithConsensus ? 'bg-emerald' : 'bg-rose-light',
                          )}
                          style={{ width: `${vote.confidence}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-text-muted">{vote.confidence}%</span>
                    </div>
                    {vote.reason && (
                      <p className="text-[11px] text-text-secondary mt-0.5 line-clamp-1">{vote.reason}</p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Bull vs Bear */}
          <div className="grid grid-cols-2 gap-2">
            {consensus.bull_case && (
              <div className="bg-emerald/5 border border-emerald/10 rounded-md px-2.5 py-2">
                <div className="flex items-center gap-1 mb-1">
                  <TrendingUp size={11} className="text-emerald" aria-hidden />
                  <span className="text-[10px] font-semibold text-emerald uppercase">Bull Case</span>
                </div>
                <p className="text-[11px] text-text-secondary leading-snug">{consensus.bull_case}</p>
              </div>
            )}
            {consensus.bear_case && (
              <div className="bg-rose/5 border border-rose/10 rounded-md px-2.5 py-2">
                <div className="flex items-center gap-1 mb-1">
                  <TrendingDown size={11} className="text-rose-light" aria-hidden />
                  <span className="text-[10px] font-semibold text-rose-light uppercase">Bear Case</span>
                </div>
                <p className="text-[11px] text-text-secondary leading-snug">{consensus.bear_case}</p>
              </div>
            )}
          </div>

          {/* Risk-adjusted confidence */}
          {consensus.risk_adjusted_confidence > 0 && (
            <div className="flex items-center gap-2 text-xs text-text-muted">
              <Shield size={11} aria-hidden />
              Risk-adjusted confidence:
              <span className="font-semibold text-text-primary">{consensus.risk_adjusted_confidence}%</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const REC_COLUMNS: ColumnDef<Recommendation>[] = [
  { header: 'Date',       accessor: row => safeFormat(row.created_at, 'yyyy-MM-dd HH:mm', format) },
  { header: 'Agent',      accessor: 'agent_name' },
  { header: 'Action',     accessor: 'action' },
  { header: 'Ticker',     accessor: 'ticker' },
  { header: 'Title',      accessor: 'title' },
  { header: 'Confidence', accessor: row => `${Math.round(row.confidence * 100)}%` },
  { header: 'Priority',   accessor: 'priority' },
  { header: 'Status',     accessor: 'status' },
  { header: 'Reasoning',  accessor: 'reasoning' },
]

export default function RecommendationsPage() {
  const {
    recommendations, filter, loading, approving, rejecting, error,
    setFilter, fetchRecommendations, approve, reject,
  } = useRecommendations()

  const { user } = useAuth()
  const canApprove = user?.permissions?.can_approve ?? false
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchRecommendations()
    const interval = setInterval(fetchRecommendations, 30_000)
    return () => clearInterval(interval)
  }, []) // eslint-disable-line

  const visibleRecs = searchQuery
    ? recommendations.filter(r =>
        (r.ticker ?? '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        r.agent_name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
    : recommendations

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Recommendations"
        subtitle="Review and approve agent recommendations before execution"
        actions={
          <>
            <TableSearch placeholder="Search ticker or agent..." onSearch={setSearchQuery} />
            <ExportCSV
              data={visibleRecs}
              columns={REC_COLUMNS}
              filename={`recommendations-${filter}`}
            />
            <button
              onClick={fetchRecommendations}
              disabled={loading}
              className="btn-secondary gap-1.5 text-xs"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} aria-hidden />
              {loading ? 'Loading...' : 'Refresh'}
            </button>
          </>
        }
      />

      {/* Filter tabs */}
      <div className="flex gap-1 glass rounded-lg p-1 w-fit">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={clsx(
              'px-4 py-2 rounded-md text-sm font-medium transition-colors',
              filter === tab.key
                ? 'bg-blue/10 text-blue-light'
                : 'text-text-secondary hover:text-text-primary hover:bg-surface-hover',
            )}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5">
          <p className="text-sm text-rose-light">{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && recommendations.length === 0 && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="h-44 skeleton" />
            <div className="h-44 skeleton" />
            <div className="h-44 skeleton" />
            <div className="h-44 skeleton" />
          </div>
          <p className="text-text-muted text-sm text-center">Loading recommendations...</p>
        </div>
      )}

      {/* Empty state */}
      {!loading && visibleRecs.length === 0 && (
        <div className="card p-12 text-center">
          <CheckCircle size={32} className="mx-auto mb-3 text-text-muted opacity-30" aria-hidden />
          <p className="text-text-muted text-sm">
            {searchQuery
              ? 'No recommendations match your search.'
              : filter === 'pending'
                ? 'No pending recommendations. Run an analysis to generate new ones.'
                : `No ${filter} recommendations found.`}
          </p>
        </div>
      )}

      {/* Recommendation cards */}
      <div className="grid gap-4 md:grid-cols-2 stagger-children">
        {visibleRecs.map(rec => {
          const agentColor = AGENT_COLORS[rec.agent_id] ?? 'bg-surface-card border-border text-text-muted'
          const acronym = AGENT_ACRONYMS[rec.agent_id] ?? rec.agent_id.toUpperCase().slice(0, 3)
          const priority = PRIORITY_BADGE[rec.priority] ?? PRIORITY_BADGE[2]
          const isPending = rec.status === 'pending'
          const isApproving = approving === rec.id
          const isRejecting = rejecting === rec.id

          return (
            <div key={rec.id} className="card card-hover p-5 flex flex-col gap-3">
              {/* Top row: agent badge + action + priority */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className={clsx(
                  'w-8 h-8 rounded-lg border flex items-center justify-center text-[10px] font-bold shrink-0',
                  agentColor,
                )}>
                  {acronym}
                </div>
                <span className="text-sm font-medium text-text-primary flex-1 min-w-0 truncate">
                  {rec.agent_name}
                </span>
                <span className={clsx('badge text-xs', ACTION_BADGE[rec.action] ?? 'badge-blue')}>
                  {rec.action}
                </span>
                <PortfolioFitBadge fit={rec.portfolio_fit} />
                <span className={clsx('badge text-xs', priority.cls)}>
                  {priority.label}
                </span>
              </div>

              {/* Title */}
              <h3 className="text-sm font-semibold text-text-primary leading-snug">
                {rec.title}
              </h3>

              {/* Ticker + confidence */}
              {rec.ticker && (
                <div className="flex items-center gap-3">
                  <span className="bg-surface-hover px-2 py-0.5 rounded">
                    <TickerLink ticker={rec.ticker!} className="text-xs" />
                  </span>
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-text-muted">Confidence:</span>
                    <span className="text-xs font-medium text-emerald">{rec.confidence}%</span>
                    <div className="w-12 h-1.5 bg-surface-hover rounded-full">
                      <div
                        className="h-full bg-emerald rounded-full"
                        style={{ width: `${rec.confidence}%` }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Reasoning */}
              <p className="text-sm text-text-secondary leading-relaxed line-clamp-3">
                {rec.reasoning}
              </p>
              <PortfolioFitNote fit={rec.portfolio_fit} />

              {/* Consensus transparency */}
              {rec.consensus && (
                <ConsensusPanel consensus={rec.consensus} />
              )}

              {/* Timestamp */}
              <span className="text-xs text-text-muted">
                {formatDistanceToNow(new Date(rec.created_at), { addSuffix: true })}
              </span>

              {/* Execution status for approved items */}
              {rec.status === 'approved' && rec.execution_result && (
                <div className="flex items-center gap-2 text-xs bg-emerald/5 border border-emerald/20 rounded-lg px-3 py-2">
                  <Shield size={12} className="text-emerald shrink-0" aria-hidden />
                  <span className="text-emerald">
                    Compliance {'\u2713'} {'\u2192'} Risk {'\u2713'} {'\u2192'} Executed
                  </span>
                </div>
              )}

              {rec.status === 'approved' && !rec.execution_result && (
                <div className="flex items-center gap-2 text-xs bg-amber/5 border border-amber/20 rounded-lg px-3 py-2">
                  <Loader2 size={12} className="text-amber animate-spin shrink-0" aria-hidden />
                  <span className="text-amber">Processing execution pipeline...</span>
                </div>
              )}

              {rec.status === 'rejected' && (
                <div className="flex items-center gap-2 text-xs bg-rose/5 border border-rose/20 rounded-lg px-3 py-2">
                  <XCircle size={12} className="text-rose-light shrink-0" aria-hidden />
                  <span className="text-rose-light">
                    Rejected{rec.reject_reason ? `: ${rec.reject_reason}` : ''}
                  </span>
                </div>
              )}

              {rec.status === 'executed' && (
                <div className="flex items-center gap-2 text-xs bg-emerald/5 border border-emerald/20 rounded-lg px-3 py-2">
                  <Activity size={12} className="text-emerald shrink-0" aria-hidden />
                  <span className="text-emerald">
                    Trade executed {rec.executed_at ? safeFormat(rec.executed_at, 'HH:mm, d MMM', format) : ''}
                  </span>
                </div>
              )}

              {/* Action buttons */}
              {isPending && canApprove && (
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={() => approve(rec.id)}
                    disabled={isApproving || isRejecting}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      'bg-emerald/10 text-emerald hover:bg-emerald/20 border border-emerald/20',
                      (isApproving || isRejecting) && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    {isApproving ? (
                      <Loader2 size={14} className="animate-spin" aria-hidden />
                    ) : (
                      <CheckCircle size={14} aria-hidden />
                    )}
                    {isApproving ? 'Approving...' : 'Approve'}
                  </button>
                  <button
                    onClick={() => reject(rec.id)}
                    disabled={isApproving || isRejecting}
                    className={clsx(
                      'flex-1 flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                      'bg-rose/10 text-rose-light hover:bg-rose/20 border border-rose/20',
                      (isApproving || isRejecting) && 'opacity-50 cursor-not-allowed',
                    )}
                  >
                    {isRejecting ? (
                      <Loader2 size={14} className="animate-spin" aria-hidden />
                    ) : (
                      <XCircle size={14} aria-hidden />
                    )}
                    {isRejecting ? 'Rejecting...' : 'Reject'}
                  </button>
                </div>
              )}

              {isPending && !canApprove && (
                <div className="flex items-center gap-2 text-xs bg-amber/5 border border-amber/20 rounded-lg px-3 py-2 mt-1">
                  <Shield size={12} className="text-amber shrink-0" aria-hidden />
                  <span className="text-amber">Only the operator can approve trades</span>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
